import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import SimpleHeader from '../../../components/Headers/SimpleHeader';
import QueueService from 'views/Queue/service/index.js';
import { useHistory, useParams } from 'react-router-dom';
import Service from '../service';
import { FlexController } from '../../../components/FlexControllerInput';
import { FlexControllerV2 } from '../../../components/FlexControllerInput/V2';
import ReactButton from 'components/Buttons/ReactButton';
import {
  Container,
  Row,
  Card,
  Col,
  Input,
  CardHeader,
  CardBody,
  FormGroup,
  Form
} from 'reactstrap';
import { toast } from 'react-toastify';
import Loader from 'react-loader-spinner';
import { useSelector } from 'react-redux';

const weekDays = [
  { key: 'monday', label: 'Segunda' },
  { key: 'tuesday', label: 'Terça' },
  { key: 'wednesday', label: 'Quarta' },
  { key: 'thursday', label: 'Quinta' },
  { key: 'friday', label: 'Sexta' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' }
];

const defaultDayState = weekDays.reduce((acc, day) => {
  acc[day.key] = false;
  acc[`${day.key}Time`] = '';
  return acc;
}, {});

export function AutomaticCloseChatConfigForm(props) {
  const params = useParams();
  const id = props.id || params.id;
  const isEdit = !!id;
  const history = useHistory();
  const [daysState, setDaysState] = useState(defaultDayState);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(isEdit);

  const { control, handleSubmit, errors, reset } = useForm({
    defaultValues: {
      queueId: '',
      closeMessage: '',
      forceEndInAttendanceChat: false
    }
  });

  const handleGetQueues = async () => {
    try {
      const response = await QueueService.getAll();
      const chatQueues = response.data.data.filter((queue) => queue.is_chat);
      setQueues(chatQueues);
    } catch (error) {
      toast.error('Erro ao buscar filas de chat!');
    }
  };

  const loadConfig = async () => {
    if (!isEdit) return;
    try {
      const response = await Service.findById(id);
      const config = response.data.data;
      const selectedQueue = queues.find((q) => q.id === config.queueId);

      reset({
        queueId: selectedQueue
          ? { label: selectedQueue.name, value: selectedQueue.id }
          : config.queue
          ? { label: config.queue.name, value: config.queue.id }
          : { label: config.queueName, value: config.queueId },
        closeMessage: config.closeMessage || '',
        forceEndInAttendanceChat: !!config.forceEndInAttendanceChat
      });

      const newDayState = { ...defaultDayState };
      weekDays.forEach((day) => {
        if (config[day.key]) {
          newDayState[day.key] = true;
          newDayState[`${day.key}Time`] = config[day.key].slice(0, 5);
        }
      });
      setDaysState(newDayState);
    } catch (error) {
      toast.error('Erro ao carregar configuração!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetQueues();
  }, []);

  useEffect(() => {
    if (isEdit && queues.length > 0) {
      loadConfig();
    }
  }, [queues, isEdit]);

  const handleDayChange = (dayKey) => {
    setDaysState((prev) => {
      const checked = !prev[dayKey];
      return {
        ...prev,
        [dayKey]: checked,
        [`${dayKey}Time`]: checked ? prev[`${dayKey}Time`] : ''
      };
    });
  };

  const handleTimeChange = (dayKey, value) => {
    setDaysState((prev) => ({
      ...prev,
      [`${dayKey}Time`]: value
    }));
  };

  const formatHour = (value) => {
    if (!value) return '';
    const [hh = '00', mm = '00'] = value.split(':');
    return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00`;
  };

  const onSubmit = async (data) => {
    const payload = {
      queueId: data.queueId.value,
      closeMessage: data.closeMessage,
      forceEndInAttendanceChat: data.forceEndInAttendanceChat
    };

    weekDays.forEach((day) => {
      if (daysState[day.key]) {
        payload[day.key] = formatHour(daysState[`${day.key}Time`]);
      } else {
        payload[day.key] = null;
      }
    });

    try {
      if (isEdit) {
        await Service.update(id, payload);
        toast.success('Configuração atualizada com sucesso!');
      } else {
        await Service.create(payload);
        toast.success('Configuração criada com sucesso!');
      }
      history.push(`/admin/closeChatConfiguration`);
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar configuração!');
    }
  };

  const cancelAction = () => {
    history.push('/admin/closeChatConfiguration');
  };

  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  if (loading)
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', marginTop: '20%' }}
      >
        <Loader
          type="TailSpin"
          color={systemConfiguration.primary_color}
          height={80}
          width={80}
        />
      </div>
    );

  return (
    <>
      <SimpleHeader returnPath="/admin/closeChatConfiguration" />
      <Container className="mt--6" fluid>
        <Row className="mt-3 mx-2">
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3>
                    {isEdit
                      ? 'Editar Configuração de Encerramento Automático'
                      : 'Configuração de Encerramento Automático'}
                  </h3>
                </CardHeader>
                <CardBody>
                  <Form onSubmit={handleSubmit(onSubmit)}>
                    <Row className="mb-3">
                      <Col md="5">
                        <FormGroup>
                          <FlexController
                            type="selectable"
                            name="queueId"
                            control={control}
                            textoDoLabel="Fila"
                            required={true}
                            errors={errors}
                            rules={{ required: 'Campo obrigatório' }}
                            options={queues.map((queue) => ({
                              label: queue.name,
                              value: queue.id
                            }))}
                            placeholder="Selecione uma fila"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <FlexControllerV2
                            type="text"
                            name="closeMessage"
                            control={control}
                            label="Mensagem de encerramento"
                            errors={errors}
                            rules={{ required: 'Campo obrigatório' }}
                            placeholder="Mensagem enviada ao cliente ao encerrar a conversa"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col>
                        <h4 className="mb-3">Horários de Encerramento</h4>
                      </Col>
                    </Row>
                    <Row style={{ textAlign: 'center' }}>
                      {weekDays.map((day) => (
                        <Col className="col-sm" key={day.key}>
                          <FormGroup>
                            <h5>
                              <input
                                type="checkbox"
                                checked={daysState[day.key]}
                                className="checkbox-round"
                                onChange={() => handleDayChange(day.key)}
                                style={{ marginRight: '0.5rem' }}
                              />
                              {day.label}
                            </h5>
                          </FormGroup>
                          <FormGroup>
                            <Input
                              type="time"
                              value={daysState[`${day.key}Time`]}
                              onChange={(e) =>
                                handleTimeChange(day.key, e.target.value)
                              }
                              disabled={!daysState[day.key]}
                              placeholder="HH:mm:ss"
                              required={daysState[day.key]}
                            />
                          </FormGroup>
                        </Col>
                      ))}
                    </Row>
                    <Row>
                      <Col className="mb-3">
                        <FormGroup check>
                          <label>
                            <Controller
                              name="forceEndInAttendanceChat"
                              control={control}
                              render={({ onChange, value }) => (
                                <Input
                                  type="checkbox"
                                  checked={value}
                                  onChange={(e) => onChange(e.target.checked)}
                                />
                              )}
                            />
                            Forçar encerramento para conversas em atendimento
                          </label>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <ReactButton btnColor="confirmation" type="submit">
                          {isEdit ? 'Atualizar' : 'Salvar'}
                        </ReactButton>
                        <ReactButton
                          btnColor="cancelation"
                          onClick={cancelAction}
                        >
                          Cancelar
                        </ReactButton>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
}

export default AutomaticCloseChatConfigForm;
